import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './SubtitlesItemsList.module.scss';
import SubtitleActions from '../SubtitleActions/SubtitleActions';
import SubtitlesItem from '../SubtitlesItem/SubtitlesItem';
import { CustomButton } from '../../../../components/Button/Button';
import { TIME_CORRECTION_STEP, VOICEOVER_COVERAGE_TYPES } from '../../const';
import { useTranslation } from 'react-i18next';
import { convertSeconds } from '../../../../utils/TimeUtil';
import { checkForFullCoverage, getItemOverlappedBy } from '../../utils';
import { MEDIA_TYPES, CLIPS_TYPE, VOICEOVER_TYPE } from '../../../../constants/clipsConstants';

const DEFAULT_MARGIN = 32;
const GAP_VALUE = 16;

const SubtitlesItemsList = ({
  activeSubtitle,
  sceneId = null,
  segmentId = null,
  subtitles,
  isGenerated,
  sceneOverlappedBy,
  limits,
  isLastScene,
  updateSubtitle,
  handleSeekToSubtitle,
  handleAddSubtitle,
  isTranslation,
  rtl,
  searchValue,
  readOnly,
  handleSwitchToEditor,
}) => {
  const emptySceneRef = useRef();

  const filteredSubtitles = useMemo(() => subtitles?.filter(item => item.hasOwnProperty('trimmedData')).map(item => ({ ...item, overlappedBy: getItemOverlappedBy(item.trimmedData, sceneOverlappedBy) })), [subtitles]);

  const { t } = useTranslation();

  const checkIsIndexTheLast = useCallback(index => filteredSubtitles.slice(index + 1).length === 0, [filteredSubtitles]);

  const itemIdToUpdate = useMemo(() => sceneId ? { sceneId } : { segmentId }, [sceneId, segmentId]);

  const isAddPossible = useCallback((item, index) => {
    const isLastIndex = checkIsIndexTheLast(index);
    if (item.overlappedBy?.length) {
      // if there is no fully overlap
      const isFullyOverlapped = checkForFullCoverage(item.overlappedBy);
      if (isFullyOverlapped) {
        // If fully overlapped, check if there is a space between voiceover end and next subtitle start, 
        // Add is not possible if the following scene is also overlapped
        if (isLastIndex) {
          return item.overlappedBy[0].end + TIME_CORRECTION_STEP <= limits.end;
        } else {
          const followingSubtitle = filteredSubtitles[index + 1];
          return checkForFullCoverage(followingSubtitle.overlappedBy) ? false : followingSubtitle.trimmedData.start - item.overlappedBy[0].end >= TIME_CORRECTION_STEP;
        }
      } else {
        switch (item.overlappedBy[0].coverage) {
          case VOICEOVER_COVERAGE_TYPES.END:
            let isPossible = item.overlappedBy[0].start - item.trimmedData.end >= TIME_CORRECTION_STEP;
            if (!isPossible && sceneOverlappedBy) {
              // check scene subtitles after the voiceover
              const subtitlesAfterVoiceover = filteredSubtitles.filter(item => item?.trimmedData?.start >= sceneOverlappedBy[0]?.end);
              if (subtitlesAfterVoiceover.length) {
                // If there is subtitles after the voiceover, disable the button
                return subtitlesAfterVoiceover[0].trimmedData.start - sceneOverlappedBy[0].end >= TIME_CORRECTION_STEP;
              }
              return limits.end - sceneOverlappedBy[0].end >= TIME_CORRECTION_STEP;
            }
            return isPossible;
          case VOICEOVER_COVERAGE_TYPES.START:
            return item.trimmedData.end - item.overlappedBy[0].start >= TIME_CORRECTION_STEP;
          case VOICEOVER_COVERAGE_TYPES.INTERMEDIATE:
            if (item.trimmedData.start >= item.overlappedBy[0].start) {
              return item.trimmedData.end - item.overlappedBy[0].start >= TIME_CORRECTION_STEP;
            }
          default: return true;
        }
      }
    } else {
      if (isLastIndex) {
        return item.trimmedData?.end + TIME_CORRECTION_STEP < limits.end;
      } else {
        const followingSubtitle = filteredSubtitles[index + 1];
        if (followingSubtitle) {
          return followingSubtitle?.trimmedData?.start - item.trimmedData?.end >= TIME_CORRECTION_STEP
        }
        return followingSubtitle?.overlappedBy[0]?.start < followingSubtitle?.trimmedData.start ? followingSubtitle?.overlappedBy[0]?.start - item.trimmedData?.end >= TIME_CORRECTION_STEP : followingSubtitle?.trimmedData?.start - item.trimmedData?.end >= TIME_CORRECTION_STEP;
      }
    }
  }, [filteredSubtitles, sceneOverlappedBy, limits]);

  const isAddNewFirstPossible = useCallback((start) => {
    if (sceneOverlappedBy?.length) {
      switch (sceneOverlappedBy[0].coverage) {
        case VOICEOVER_COVERAGE_TYPES.START:
          return start - sceneOverlappedBy[0].end >= TIME_CORRECTION_STEP;
        case VOICEOVER_COVERAGE_TYPES.INTERMEDIATE:
          return start > limits.start && (start - sceneOverlappedBy[0].end >= TIME_CORRECTION_STEP || sceneOverlappedBy[0].start - limits.start >= TIME_CORRECTION_STEP);
        default: return false;
      }
    } else {
      return start - limits.start >= TIME_CORRECTION_STEP
    }
  }, [limits, sceneOverlappedBy]);

  const handleChangeText = useCallback((id, value, translated = false) => {
    updateSubtitle({ ...itemIdToUpdate, id, key: 'text', value, translated });
  }, [updateSubtitle]);

  const handleChangeStartTime = useCallback((id, value, translated) => {
    updateSubtitle({ ...itemIdToUpdate, id, key: 'start', value, translated });
  }, [updateSubtitle]);

  const handleChangeEndTime = useCallback((id, value, translated) => {
    updateSubtitle({ ...itemIdToUpdate, id, key: 'end', value, translated });
  }, [updateSubtitle]);

  const handleMergeSubtitle = useCallback(id => {
    updateSubtitle({ ...itemIdToUpdate, id, key: 'merge' });
  }, [updateSubtitle]);

  const handleDeleteSubtitle = useCallback(id => {
    updateSubtitle({ ...itemIdToUpdate, id, key: 'delete' });
  }, [updateSubtitle]);

  const onAddSubtitle = useCallback(id => {
    handleAddSubtitle({ ...itemIdToUpdate, id });
  }, [handleAddSubtitle]);

  const showAddButton = () => {
    if (emptySceneRef?.current) {
      const buttonElement = emptySceneRef.current.querySelector('button');
      emptySceneRef.current.style.height = `${emptySceneRef.current.offsetHeight + buttonElement?.offsetHeight + GAP_VALUE}px`;
    }
  };

  const hideAddButton = () => {
    if (emptySceneRef?.current) {
      const messageElement = emptySceneRef.current.querySelector('p');
      emptySceneRef.current.style.height = `${messageElement.offsetHeight + GAP_VALUE + DEFAULT_MARGIN}px`;
    }
  };

  useEffect(() => {
    setTimeout(() => {
      hideAddButton();
    }, 50);
  }, [emptySceneRef?.current?.offsetWidth]);

  return filteredSubtitles?.length ? (
    filteredSubtitles.map(({ trimmedData, data, key, overlappedBy = null }, index) => (
      <Fragment key={key}>
        {!readOnly && index === 0 && (
          <SubtitleActions
            addSubtitle={() => onAddSubtitle(null)}
            isAddPossible={isAddNewFirstPossible(trimmedData.start)}
            showMerge={false}
            isFirstSubtitle={true}
          />
        )}
        <SubtitlesItem
          index={index}
          id={key}
          active={activeSubtitle?.key === key}
          isTranslation={isTranslation}
          originalData={data}
          data={trimmedData}
          adjacentItems={{
            prevItem: filteredSubtitles[index - 1] ?? null,
            nextItem: filteredSubtitles[index + 1] ?? null,
            sceneLimits: limits,
          }}
          itemOverlappedBy={overlappedBy}
          updateText={handleChangeText}
          updateStartStamp={handleChangeStartTime}
          updateEndStamp={handleChangeEndTime}
          addSubtitle={onAddSubtitle}
          mergeSubtitle={handleMergeSubtitle}
          deleteSubtitle={handleDeleteSubtitle}
          searchValue={searchValue}
          handleSeekToSubtitle={handleSeekToSubtitle}
          rtl={rtl}
          subtitlesLength={filteredSubtitles?.length}
          isMergePossible={filteredSubtitles.slice(index + 1).length > 0 && !(checkForFullCoverage(overlappedBy) || checkForFullCoverage(filteredSubtitles[index + 1]?.overlappedBy))
          }
          isAddPossible={isAddPossible({ trimmedData, data, overlappedBy }, index)}
          isSceneLastItem={checkIsIndexTheLast(index) && !isLastScene}
          readOnly={readOnly}
          voiceoverItem={readOnly && sceneId === null}
          handleSwitchToEditor={handleSwitchToEditor}
        />
      </Fragment>
    ))
  ) : (
    <div ref={emptySceneRef} className={styles.noSpeechScene} onMouseEnter={showAddButton} onMouseLeave={hideAddButton}>
      <p className={styles.message}>
        {t(isGenerated ? 'subtitles.editor.no_audible_speech_scene' : 'subtitles.editor.no_generated_speech_scene', {
          time: `${convertSeconds(limits?.start / 1000)} - ${convertSeconds(limits?.end / 1000)}`,
          item: t(sceneId ? MEDIA_TYPES[CLIPS_TYPE] : MEDIA_TYPES[VOICEOVER_TYPE]),
        })}
      </p>
        {!readOnly && isGenerated && <CustomButton
        label={t('subtitles.editor.add_subtitle')}
        variant="primary"
        onClick={() => handleAddSubtitle({ ...itemIdToUpdate, id: null })}
        className={styles.addBtn}
      />}
    </div>
  );
};

export default SubtitlesItemsList;
